import {useCallback, useEffect, useState} from 'react';
import {
  Box,
  Button,
  Divider,
  FormLabel,
  IconButton,
  makeStyles,
  Popover,
  TextField,
  useMediaQuery,
  useTheme,
  withStyles
} from '@material-ui/core';
import ImmersionModal from '../modals/commonModal';
import AnalysisScoreIcon from './analyzeScoreIcon';
import {useHistory} from 'react-router';
import {useDispatch, useSelector} from 'react-redux';
import UtilService from '../../../helper/service';
import roles from '../../../helper/roles';
import {
  getSegmentAsClip,
  onCallClipAPI,
  optimizeLiveGraph,
  updateClipedData,
  updateSettings
} from '../../../store/actions/creator/analysis';
import {getSessionParticipants} from "../../../store/actions/creator/creatorExperienceManager";

const useStyles = makeStyles((theme) => ({
  typography: {
    padding: theme.spacing(2),
  },
  clock: {
    width: 12,
    height: 12,
    marginRight: 3
  },
  timeLabel: {
    fontFamily: 'Spartan-extrabold',
    fontSize: 10,
    marginRight: 3
  },
  segmentWord: {
    fontFamily: 'Spartan-bold',
    fontSize: 8,
    textTransform: 'uppercase',
    overflow: 'hidden',
    whiteSpace: 'nowrap'
  },
  popover: {
    background: '#fff',
    width: 226,
    borderRadius: 10,
    // marginTop: 6,
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    fontFamily: 'roboto-bold',
    color: '#1DB859',
    fontSize: 12,
    margin: 0,
    '& p:first-of-type': {
      margin: '0 7px',
      fontSize: 18,
    },
    '& p:nth-of-type(2)': {
      fontFamily: 'Spartan-bold',
      letterSpacing: 1.2,
      paddingTop: 4,
      textTransform: 'uppercase',
    }
  },
  smallRow: {
    display: 'flex',
    flexDirection: 'row',
    flex: 1,
    alignItems: 'center',
    fontFamily: 'roboto-bold',
    color: '#8191A1',
    '& img': {
      width: 12,
      height: 12,
      marginRight: 3
    }
  },
  pane: {
    display: 'flex',
    flexDirection: 'row',
    fontFamily: 'Spartan-bold',
    fontSize: 8,
    color: '#8191A1',
    marginTop: 6
  },
  title: {
    fontFamily: 'roboto-bold',
    color: '#606B76',
    fontSize: 14
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    margin: '0 0 -2px 12px'
  },
  btnBox: {
    display: 'flex',
    justifyContent: 'flex-end'
  },
  btn: {
    marginTop: 18,
    marginLeft: 5,
    padding: '3px 4px 3px 4px',
    color: '#697683',
    fontFamily: 'roboto-regular',
    fontSize: 10,
    background: '#F0F0F0',
    borderRadius: 6,
    textTransform: 'none',
    '&:hover': {
      background: '#e7e5e5',
    },
    '& img': {
      marginRight: 3
    }
  },
  label: {
    fontSize: 12,
    fontFamily: 'roboto-bold',
    color: '#697683',
    textAlign: 'left',
  },
  icon: {
    width: 20,
    height: 20
  },
  pop: {
    '& .MuiPaper-rounded': {
      borderRadius: 10
    },
    '& .MuiPaper-elevation8': {
      boxShadow: '0px 0px 10px #00000033'
    }
  }
}));

const CssTextField = withStyles({
  root: {
    '& label.MuiFormLabel-root': {
      color: '#697683',
      fontFamily: 'roboto-bold',
      fontSize: 12,
    },
    '& .MuiInputBase-input': {
      fontFamily: 'roboto-regular',
      fontSize: 14,
      color: '#8F99A3'
    }
  },
})(TextField);

function AnalyzeKeyMomentPopOut(props: any) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();

  const { overlay, onZoom, anchorEl, setAnchorEl, onboarding, overview, goAnalyzeTab } = props;
  const { currentSession, stimulus, clips, settings, cliped } = useSelector((state: any) => state.analysis);
  const apolloClient = useSelector((state: any) => state.apolloClient);
  const { session_code, session_type, session_length } = currentSession;
  const { graphZoom } = settings;
  const [newClipId, setNewClipId] = useState<any>();
  const open = Boolean(anchorEl);
  const id = open ? 'simple-popper' : undefined;

  const loginUserData = localStorage.getItem("user_data") ? JSON.parse(localStorage.getItem("user_data") || "{}") : "";

  const [clipCreateModal, setClipCreateModal] = useState<boolean>(false);
  const [clipViewModal, setClipViewModal] = useState<boolean>(false);
  const [clipName, setClipName] = useState<string>('');
  const [textFocus, setTextFocus] = useState(true);

  const theme = useTheme();
  const isMobileDevice = useMediaQuery(theme.breakpoints.down('xs'));

  const handleUserKeyPress = (event: any) => {
    if (event.keyCode === 13 && clipCreateModal ) {
      setTextFocus(false);
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleUserKeyPress);
    return () => {
      window.removeEventListener('keydown', handleUserKeyPress);
    };
  });

  const handleClose = () => {
    setAnchorEl(null);
    if(overview || onboarding){
      return false;
    }
    const userData = localStorage.getItem("user_data") ? JSON.parse(localStorage.getItem("user_data") || "{}") : "";
    let url = `/creator/experience-manager/analyze/${session_code}`;
    if (!!userData) {
      if (UtilService.checkTrialAccess(userData, roles['trial']['experiences'])) {
        url = `/subscriber/creator/experience-manager/analyze/${session_code}`;
      }
    }
    history.push({
      pathname: url,
      state: { data: null }
    })
  };

  const onClip = async () => {
    if (clipName) {
      const request = {
        stimulus_id: session_type === 'ASYNC' ? stimulus.stimulus_id : session_code,
        type: session_type === 'ASYNC' ? 'clip-async' : 'clip-moderated',
        session_code: session_code,
        clip: {
          title: clipName,
          start_clip: (session_type === 'ASYNC') ? UtilService.calcVideoEnd(overlay?.start) : String(overlay?.start),
          end_clip: (session_type === 'ASYNC') ? UtilService.calcVideoEnd(overlay?.end) : String(overlay?.end)
        }
      };

      dispatch(updateSettings('clipLoading', true))

      try {
        dispatch(onCallClipAPI(request, loginUserData.customer_id, session_code, (res: any) => {
          if (res) {
            setTimeout(() => {
              getSessionClips();
              fetchClips();
              setNewClipId(res.clip_id);
              setClipViewModal(true);
              setClipCreateModal(false);
              dispatch(updateSettings('clipLoading', false));
            }, 7000);
          }
        }));
      } catch (e) {
        setClipCreateModal(false)
      }
    }
  };

  const fetchClips = () => {
    const request = {
      session_code: session_code,
    };
    dispatch(getSessionParticipants(apolloClient.client, request, ()=>{}));
  };

  const getSessionClips = () => {
    if (session_code && stimulus && session_type === 'ASYNC') {
      dispatch(getSegmentAsClip(loginUserData.customer_id, stimulus.stimulus_id))
    }
    if (session_code && session_type === 'MODERATED') {
      dispatch(getSegmentAsClip(loginUserData.customer_id, session_code))
    }
  };

  const getSegment = (timeStr: string, type: string) => {
    return (
      <div className={classes.pane}>
        <div className={classes.smallRow}>
          <img src={'/assets/icons/participant/clock-deactive.svg'} alt={'icon'} className={classes.clock} />
          <span className={classes.timeLabel} >{timeStr}</span>
          <span className={classes.segmentWord}>{type}</span>
        </div>
      </div>
    )
  };

  const onViewClip = () => {
    // set clip session_id
    const userData = localStorage.getItem("user_data") ? JSON.parse(localStorage.getItem("user_data") || "{}") : "";

    setAnchorEl(null);
    let url = `/creator/experience-manager/analyze/${session_code}`;
    if (!!userData) {
      if (UtilService.checkTrialAccess(userData, roles['trial']['experiences'])) {
        url = `/subscriber/creator/experience-manager/analyze/${session_code}`
      }
    }
    history.push(url);
    setTimeout(() => {
      dispatch(updateSettings('videoPopIn', 0))
      dispatch(optimizeLiveGraph())

      dispatch(updateClipedData({
        title: clipName,
        start_clip: overlay?.start,
        end_clip: overlay?.end,
        clip_id: newClipId,
        session_code,
        score: {
          immersion: {
            immersion_index: overlay.overall_index,
            tier_indicator: overlay.context_indicator,
            color_indicator: overlay.color,
            context_indicator: overlay.context_indicator,
          },
          safety: {
            safety_index: overlay?.safety_index,
            tier_indicator: overlay?.safety_indicator,
            color_indicator: overlay?.safety_color,
            context_indicator: overlay?.safety_indicator,
          }
        }
      }))
    }, 1000);
  };

  const onClose = () => {
    setAnchorEl(null);
    setClipCreateModal(false);
    setClipViewModal(false);
  };

  const showSafety = useCallback(() => {
    const length = cliped ? Number(cliped.end_clip - cliped.start_clip) : Number(session_length);
    return UtilService.isShowSafety(session_type, length);
  }, [cliped, session_length, session_type]);

  const onClickClip = () => {
    const clip_name = overlay.type === 'Agenda' ? overlay.title : 'Clip ' + Number(clips.length + 1);
    setClipName(clip_name);

    setClipCreateModal(true);
  };

  return (
    <Popover
      id={id}
      open={open}
      anchorEl={anchorEl}
      style={{ zIndex: 5 }}
      onClose={handleClose}
      anchorOrigin={{
        vertical: overview ? 'center' : 'bottom',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      className={classes.pop}
    >
      <div className={classes.popover}>
        <Box className={classes.header}>
          <p className={classes.title}>
            {overlay.type === 'Agenda' ? overlay?.title : overlay.type + ' ' + overlay?.segment_start}
          </p>
          <IconButton aria-label="close" onClick={handleClose}>
            <img src={'/assets/icons/creatordashboard/project-modal-close.svg'} alt={'img'} style={{ width: 10, height: 10 }} />
          </IconButton>
        </Box>

        <Divider />

        <Box p={2} pt={0}>
          <div className={classes.row} style={{ color: UtilService.refactorGraphColor(overlay.color) }}>
            <AnalysisScoreIcon color={overlay.color} type={'immersion'} size={2} />
            <p>{overlay?.overall_index > 0 ? overlay?.overall_index : '--'}</p>
            <p>{overlay?.context_indicator ? overlay?.context_indicator : 'No Data'}</p>
          </div>
          { (showSafety() && overlay?.safety_index > 1) && (
            <div className={classes.row} style={{ marginTop: -10, color: UtilService.refactorGraphColor(overlay.safety_color) }}>
              <AnalysisScoreIcon color={overlay.safety_color} type={'safety'} size={2} />
              <p>{overlay?.safety_index}</p>
              <p>{overlay?.safety_indicator}</p>
            </div>
          ) }

          <SegmentBox title={'RUNTIME'} value={overlay?.segment_length} />
          <SegmentBox title={'START TIME'} value={overlay?.segment_start} />
          <SegmentBox title={'END TIME'} value={overlay?.segment_end} />

          <div className={classes.btnBox}>
            {overview && !isMobileDevice && <Button className={classes.btn} onClick={() =>goAnalyzeTab(overlay)}>
              <img src={'/assets/icons/liveanalysis/zoom-icon.svg'} alt='plus-icon' />
              View in Timeline
            </Button>}
            {!overview && graphZoom >= 1 && overlay?.type !== 'Key Moment' && <Button
              className={classes.btn}
              onClick={() => {
                setAnchorEl(null);
                onZoom(overlay?.length, overlay?.start);
              }}
            >
              <img src={'/assets/icons/liveanalysis/zoom-icon.svg'} alt='plus-icon' />
              Zoom In
            </Button>}
            {!onboarding && !isMobileDevice &&
              <Button className={classes.btn} onClick={() => onClickClip()}>
                <img src={'/assets/icons/liveanalysis/scissors-icon.svg'} alt='scissors-icon' />
                Clip
              </Button>
            }
          </div>

          <ImmersionModal
            open={clipCreateModal}
            title={'Name Your Clip'}
            description={'Give your clip a name then save it. Hint: You can always find your Clips in the “My Experiences” dashboard.'}
            titleOK={'Save'}
            cancelDisable={true}
            onClose={onClose}
            focused={textFocus}
            additional={
              <div style={{ margin: '40px 40px 40px 0' }}>
                <FormLabel className={classes.label}>
                  Clip Name
                </FormLabel>
                <br />
                <CssTextField
                  fullWidth
                  name="clip"
                  value={clipName}
                  onChange={e => setClipName(e.target.value)}
                  autoFocus
                  onFocus={event => {
                    event.target.select()
                  }}
                />
              </div>
            }
            onApply={() => {
              onClip();
            }}
          />

          <ImmersionModal
            open={clipViewModal}
            title={'✂ ' + clipName}
            titleOK={'View Clip'}
            cancelDisable={true}
            okDisable={false}
            onClose={onClose}
            additional={
              overlay?.color &&
              <div style={{ margin: '20px 40px 40px 0' }}>
                <div>
                  <div className={classes.row} style={{ color: UtilService.refactorGraphColor(overlay.color) }}>
                    <AnalysisScoreIcon color={overlay.color} type={'immersion'} size={2} />
                    <p>{overlay?.overall_index}</p>
                    <p>{overlay?.context_indicator ? overlay?.context_indicator : '--'}</p>
                  </div>

                  <div className={classes.row} style={{ marginTop: -10, color: UtilService.refactorGraphColor(overlay?.safety_index > 1 ? overlay.safety_color : 'gray') }}>
                    <AnalysisScoreIcon color={overlay?.safety_index > 1 ? overlay.safety_color : 'gray'} type={'safety'} size={2} />
                    <p>{overlay?.safety_index}</p>
                    <p>{overlay?.safety_indicator ? overlay?.safety_indicator : ''}</p>
                  </div>
                </div>
                {overlay &&
                  <>
                    {getSegment(overlay?.segment_length, 'runtime')}
                    {getSegment(overlay?.segment_start, 'start time')}
                    {getSegment(overlay?.segment_end, 'stop time')}
                  </>
                }

              </div>

            }
            onApply={() => {
              setClipViewModal(false);
              onViewClip();
            }}
          />
        </Box>
      </div>
    </Popover>

  );
}

export default AnalyzeKeyMomentPopOut;

function SegmentBox(props: any) {
  const classes = useStyles();
  return (
    <div className={classes.pane}>
      <div className={classes.smallRow}>
        <img src={'/assets/icons/participant/clock-deactive.svg'} alt={'icon'} />
        <span style={{ marginRight: 3 }}>{props.value}</span>
        <span> {props.title}</span>
      </div>
    </div>
  )
}